import { Chip, Grid } from '@mui/material'
import { Box } from '@mui/system'
import { useCallback, useContext, useEffect, useState } from 'react'
import {
  CustomInput,
  CustomSelect,
  CustomTextArea,
  selectTypes,
} from 'src/consumer/components/helperComponents'
import { UNIQUE_COLORS_100 } from 'src/consumer/constants'
import {
  checkAndGetTargetValue,
  generateUniqueHexWithOpacity,
} from 'src/consumer/helpers/utilFunctions'
import { useScreenSize } from 'src/customHooks/customHooks'
import { ACTIVITY_PLAN_TYPES } from 'src/mentor/components/ActivityPlanComponent/constants'
import { MentorSetupContext } from 'src/mentor/context/MentorSetupContext'
import {
  PHASE_OBJECT,
  PLANNED_ACTIVITY_OBJECT,
  checkPhasesValidity,
  fixDaysAndContent,
  fixStartDaysAndEndDays,
} from '../utils'
import { Phases } from './Phases'
import PlanDayWiseDetailsTable from './PlanDayWiseDetails'
import {
  CustomPlanSectionContainer,
  StyledAPTabButton,
  StyledAPTabContainer,
  StyledCustomPlanFormGridContainer,
  StyledSubscriptionDropDownContainer,
} from './styledComponents'
import PictureUploadComponent from 'src/consumer/components/Helpers/PictureUploadComponent/PictureUploadComponent'

export default function CustomPlansComponent({
  customPlan,
  handleChange,
  setCustomPlan,
  disabled,
  mode,
  validationObject,
  dropDownOptions,
}) {
  const { allTags } = useContext(MentorSetupContext)
  const [selectedPhaseIndex, setSelectedPhaseIndex] = useState(0)
  const [showPhaseReorder, setShowPhaseReorder] = useState(false)
  const [selectedTab, setSelectedTab] = useState(tabMap.basic)

  const { width, height } = useScreenSize()

  const changePhase = useCallback(
    (prevPhase, idx) => {
      setCustomPlan((prev) => {
        const isPrevFunc = typeof prevPhase === 'function'
        let toSet = fixStartDaysAndEndDays(
          prev?.phaseList.map((phs, phsIndex) => {
            if (phsIndex === idx) {
              return isPrevFunc ? prevPhase(phs) : prevPhase
            }
            return phs
          }),
        )
        let plannedActivitiesToUse = fixDaysAndContent(prev, toSet, prev?.plannedActivities || [])
        // setPhaseContent((prev) => fixDaysAndContent(customPlan, toSet, prev))
        return {
          ...prev,
          phaseList: toSet,
          plannedActivities: plannedActivitiesToUse,
        }
      })
    },
    [setCustomPlan],
  )
  const changePlannedActivity = useCallback(
    (eventPrev, idx) => {
      if (typeof eventPrev === 'function') {
        setCustomPlan((prev) => {
          return {
            ...prev,
            plannedActivities: prev?.plannedActivities.map((act, actIndex) => {
              return actIndex === idx ? eventPrev(act) : act
            }),
          }
        })
      } else {
        setCustomPlan((prev) => {
          return {
            ...prev,
            plannedActivities: prev?.plannedActivities.map((act, actIndex) => {
              return actIndex === idx
                ? { ...act, [eventPrev.target.id]: checkAndGetTargetValue(eventPrev) }
                : act
            }),
          }
        })
      }
    },
    [setCustomPlan],
  )

  function showReorder() {
    setShowPhaseReorder(true)
  }

  function handleChangePhase(event, idx) {
    changePhase((prev) => {
      return { ...prev, [event.target.id]: checkAndGetTargetValue(event) }
    }, idx)
  }

  function addPhase() {
    setCustomPlan((prev) => {
      console.log(prev?.phaseList?.[prev?.phaseList?.length - 1])
      const toSet = fixStartDaysAndEndDays([
        ...(prev?.phaseList || []),
        {
          ...PHASE_OBJECT,
          days: prev?.phaseList?.[prev?.phaseList?.length - 1]
            ? IfNegativeThenOne(
                customPlan?.totalDays - prev?.phaseList?.[prev?.phaseList?.length - 1]?.lastDay,
              )
            : customPlan?.totalDays,
          title: prev?.phaseList?.length ? `Phase ${(prev?.phaseList?.length || 0) + 1}` : '',
          legendColor:
            UNIQUE_COLORS_100?.[prev?.phaseList?.length || 0] || generateUniqueHexWithOpacity(1),
          _newPhase: true,
        },
      ])
      return {
        ...prev,
        phaseList: toSet,
        // plannedActivities: plannedActivitiesToUse,
      }
    })
  }

  function removePhase(idx) {
    setCustomPlan((prev) => ({
      ...prev,
      phaseList: prev?.phaseList.filter((phs, index) => idx !== index),
    }))
  }

  function activityPlanImageUploadComponent() {
    return (
      <div className="pr-2">
        <PictureUploadComponent
          value={customPlan?.coverIcon}
          onChange={(event) =>
            handleChange({ target: { id: 'coverIcon', value: event.target.value } })
          }
          title="Square Image"
          disabled={disabled}
          aspectHeight={100}
          aspectWidth={100}
        />
      </div>
    )
  }

  function handleChangeTotalDays(event) {
    const selfEvent = {
      target: {
        id: event.target.id,
        value: checkAndGetTargetValue(event) < 0 ? 0 : Math.min(checkAndGetTargetValue(event), 100),
        type: 'number',
      },
    }
    handleChange(selfEvent)
  }

  function handleTabChange(newValue) {
    setSelectedTab(newValue)
  }

  useEffect(() => {
    setCustomPlan((prev) => ({
      ...prev,
      plannedActivities: makePlannedActivityAsLongAsTotalDays(
        prev?.plannedActivities || [],
        customPlan?.totalDays || 0,
      ),
    }))
  }, [customPlan?.totalDays])

  return (
    <>
      <StyledAPTabContainer
        sx={{
          position: 'sticky',
          top: '0px',
          zIndex: '2',
          background: '#fafafa',
          borderBottom: '1px solid var(--cui-border-color)',
          padding: '15px 0px',
        }}
        value={selectedTab}
        onChange={handleTabChange}
      >
        <StyledAPTabButton
          selected={selectedTab === tabMap.basic}
          onClick={() => handleTabChange(tabMap.basic)}
        >
          Plan Basic Details
        </StyledAPTabButton>
        <StyledAPTabButton
          invalid={!checkPhasesValidity(customPlan)?.valid}
          selected={selectedTab === tabMap.phases}
          onClick={() => handleTabChange(tabMap.phases)}
        >
          Plan Phases (Optional) <Chip size={'small'} label={customPlan?.phaseList?.length || 0} />
        </StyledAPTabButton>
        <StyledAPTabButton
          selected={selectedTab === tabMap.plannedActivities}
          onClick={() => handleTabChange(tabMap.plannedActivities)}
          invalid={
            validationObject.plannedActivities?.filter((pActivity) => !pActivity.valid)?.length > 0
          }
        >
          Day Wise Planned Activities
        </StyledAPTabButton>
      </StyledAPTabContainer>
      <CustomTabPanel selectedValue={selectedTab} tabValue={tabMap?.basic}>
        <StyledCustomPlanFormGridContainer container marginTop={3} spacing={3}>
          <Grid
            xs={12}
            item
            sx={{
              display: 'flex',
              flexDirection: width >= 768 ? 'row' : 'column-reverse',
              justifyContent: width >= 768 ? 'space-between' : 'flex-start',
              gap: '30px',
            }}
          >
            <div
              className={`d-flex flex-column justify-content-start gap-2 ${
                width >= 768 ? 'w-50' : 'w-100'
              }`}
            >
              <CustomInput
                id="title"
                label="Title"
                disabled={disabled}
                value={customPlan?.title}
                required
                colNumber={12}
                placeholder="Enter the title of your plan..."
                onChange={handleChange}
              />
              <CustomTextArea
                id="description"
                label="Description"
                disabled={disabled}
                value={customPlan?.description}
                onChange={handleChange}
                placeholder={'Enter the description of your plan...'}
              />
              <CustomInput
                id="totalDays"
                label="Total Days"
                disabled={disabled}
                value={customPlan?.totalDays}
                colNumber={12}
                type="number"
                onChange={handleChangeTotalDays}
                required
              />
              <CustomSelect
                id="visibility"
                label="Visibility"
                isDisabled={disabled}
                value={customPlan?.visibility}
                options={VISIBILITY_OPTIONS}
                getOptionValue={(option) => option?.id}
                getOptionLabel={(option) => option?.title}
                colNumber={12}
                selectType={selectTypes.string}
                onChangeEvent={handleChange}
                placeholder="Select the visibility of the plan..."
                required
              />
              <CustomSelect
                id="domainIds"
                label="Domains"
                isMulti
                isDisabled={disabled}
                value={customPlan?.domainIds}
                options={allTags.sort((a, b) => a?.title.localeCompare(b?.title))}
                getOptionValue={(option) => option?.id}
                getOptionLabel={(option) => option?.title}
                colNumber={12}
                selectType={selectTypes.string}
                onChangeEvent={handleChange}
                placeholder="Select domains..."
              />
            </div>
            {activityPlanImageUploadComponent()}
          </Grid>
          <Grid xs={12} md={6} item>
            <CustomSelect
              id="type"
              label="Activity Plan Type"
              isDisabled={disabled}
              value={customPlan?.type}
              options={ACTIVITY_PLAN_TYPES}
              getOptionValue={(option) => option?.id}
              getOptionLabel={(option) => option?.title}
              colNumber={12}
              selectType={selectTypes.string}
              onChangeEvent={handleChange}
              placeholder="Select the type of activity plan..."
            />
          </Grid>
          {/* <Grid xs={12} md={3} item></Grid>
          <Grid xs={12} md={6} item></Grid> */}
          <Grid xs={12} md={6} item>
            <CustomSelect
              id="subscription"
              label="Subscription Type"
              isDisabled={disabled}
              value={customPlan?.subscription}
              options={dropDownOptions?.subscriptionLevels}
              getOptionValue={(option) => option?.id}
              getOptionLabel={(option) => option?.name}
              colNumber={12}
              selectType={selectTypes.object}
              onChangeEvent={handleChange}
              placeholder="Select the subscription level..."
              required
            />
          </Grid>
          <Grid xs={12} md={6} item>
            <StyledSubscriptionDropDownContainer>
              <CustomSelect
                id="pricePlan"
                label="Subscription Plan"
                isDisabled={disabled}
                value={customPlan?.pricePlan}
                options={dropDownOptions?.oneOnOnePlans
                  ?.filter((plan) => !plan?.recurring)
                  ?.sort((a, b) => a?.title?.localeCompare(b?.title))}
                getOptionValue={(option) => option?.id}
                getOptionLabel={(option) => option?.title}
                colNumber={12}
                selectType={selectTypes.string}
                onChangeEvent={handleChange}
                placeholder="Select Subscription Plan..."
              />
            </StyledSubscriptionDropDownContainer>
          </Grid>
          <Grid
            xs={12}
            sx={{
              padding: '25px 0px 0px 0px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '5px',
            }}
          >
            <PictureUploadComponent
              value={customPlan?.coverIcon}
              onChange={(event) =>
                handleChange({ target: { id: 'coverImage', value: event.target.value } })
              }
              title="Landscape Image"
              disabled={disabled}
              aspectWidth={720}
              aspectHeight={410}
            />
          </Grid>
        </StyledCustomPlanFormGridContainer>
      </CustomTabPanel>
      <CustomTabPanel selectedValue={selectedTab} tabValue={tabMap?.phases}>
        <CustomPlanSectionContainer>
          <Phases
            customPlan={customPlan}
            setCustomPlan={setCustomPlan}
            changePhase={changePhase}
            mode={mode}
            disabledFields={disabled}
            handleChange={handleChangePhase}
            addPhase={addPhase}
            removePhase={removePhase}
          />
        </CustomPlanSectionContainer>
      </CustomTabPanel>
      <CustomTabPanel selectedValue={selectedTab} tabValue={tabMap?.plannedActivities}>
        <CustomPlanSectionContainer>
          <PlanDayWiseDetailsTable
            plannedActivities={customPlan?.plannedActivities}
            setPlannedActivities={changePlannedActivity}
            setCustomPlan={setCustomPlan}
            allPhases={customPlan?.phaseList || []}
            // phase={customPlan?.phaseList?.[selectedPhaseIndex] || {}}
            disabled={disabled}
            totalDays={customPlan?.totalDays}
            validationObject={validationObject}
            dropDownData={dropDownOptions}
          />
        </CustomPlanSectionContainer>
      </CustomTabPanel>
      {/* <Dialog
        open={showPhaseReorder}
        PaperComponent={PhaseContentReorderPaper}
        onClose={handleClose}
      >
        <PlanContentReorderComponent
          handleClose={handleClose}
          handleUpdate={() => {}}
          content={phaseContent}
          allPhases={customPlan?.phases || []}
        />
      </Dialog> */}
    </>
  )
}

function makePlannedActivityAsLongAsTotalDays(plannedActivities, numberItShouldHave) {
  const currentLength = plannedActivities.length

  if (currentLength >= numberItShouldHave) {
    // If the array is already long enough, return it as is
    return plannedActivities
  } else {
    // If the array is shorter, add the necessary numbers to meet the desired length
    for (let i = currentLength + 1; i <= numberItShouldHave; i++) {
      plannedActivities.push(PLANNED_ACTIVITY_OBJECT)
    }
    return plannedActivities
  }
}

function CustomTabPanel({ children, selectedValue, tabValue, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={selectedValue !== tabValue}
      id={`simple-tabpanel-${tabValue}`}
      aria-labelledby={`simple-tab-${tabValue}`}
      {...other}
      className="flex-grow-1"
    >
      {selectedValue === tabValue && <Box>{children}</Box>}
    </div>
  )
}

function IfNegativeThenOne(number) {
  if (number < 0) {
    return 1
  }
  return number
}

const tabMap = {
  basic: 'basic',
  // phases: 'phases',
  plannedActivities: 'plannedActivities',
  images: 'images',
}

export const VISIBILITY_OPTIONS = [
  { id: 'free', title: 'Free' },
  { id: 'public', title: 'Public' },
]
